import React from "react";

import { ComparedModel } from "app/compare/[match]";
import { RatingsStars } from "app/smartphone/[slug]/[reference]/RatingsStars";
import { useI18n } from "utils/with-i18n.utils";

type Props = {
  left: ComparedModel;
  right: ComparedModel;
};

const getTechnicalToCompare = (left: ComparedModel, right: ComparedModel) => {
  const isIphonesComparison = [left, right].every((model) => model.slug.includes("iphone"));
  const canBeUniversalComparison = [left, right].every((model) => model.description?.technical_universal);
  const technicalKey = isIphonesComparison || !canBeUniversalComparison ? "technical" : "technical_universal";

  return Array.from(
    new Set([
      ...(left.description?.[technicalKey]?.sections ?? []).map(
        (s: { title: any }) => s.title,
      ),
      ...(right.description?.[technicalKey]?.sections ?? []).map(
        (s: { title: any }) => s.title,
      ),
    ]),
  ).map((section) => {
    const leftSection = left.description?.[technicalKey]?.sections.find(
      (s: { title: string }) => s.title === section,
    );

    const rightSection = right.description?.[technicalKey]?.sections.find(
      (s: { title: string }) => s.title === section,
    );

    const items: string[] = [];

    if (leftSection) {
      items.push(...leftSection.items.map((i: { name: string }) => i.name));
    }

    if (rightSection) {
      items.push(...rightSection.items.map((i: { name: string }) => i.name));
    }

    return {
      title: section,
      items: Array.from(new Set(items)).map((item) => {
        const leftItem = leftSection?.items?.find(
          (i: { name: string; value: string }) => i.name === item,
        )?.value;

        const rightItem = rightSection?.items?.find(
          (i: { name: string; value: string }) => i.name === item,
        )?.value;

        return {
          name: item,
          left: leftItem || "-",
          right: rightItem || "-",
        };
      }),
    };
  });
};

export const Technical = ({ left, right }: Props) => {
  const i18n = useI18n();

  if (!left.description || !right.description) {
    return null;
  }

  const featuresToCompare = getTechnicalToCompare(left, right);

  return (
    <section className="flex flex-col gap-5 mx-auto w-full max-w-[950px]">
      {/* Rating */}
      <div className="flex flex-col">
        <h4 className="mb-1 text-xl font-bold">
          {i18n.t("comparator.ratingTitle")}
        </h4>
        <h5 className="pt-2 mb-2 text-xs font-bold sm:text-sm">
          {i18n.t("comparator.ratingSubtitle")}
        </h5>
        <div className="grid grid-cols-2 gap-7 pb-2 text-sm border-b sm:!gap-28 sm:text-base 2xs:gap-20">
          <div>
            <RatingsStars rating={left.description.review?.rating ?? "0"} />
            <p className="text-[12px] font-light text-black/60">
              {i18n.t("comparator.ratingSource", {
                source: left.description.review?.source ?? "",
              })}
            </p>
          </div>
          <div>
            <RatingsStars rating={right.description.review?.rating ?? "0"} />
            <p className="text-[12px] font-light text-black/60">
              {i18n.t("comparator.ratingSource", {
                source: right.description.review?.source ?? "",
              })}
            </p>
          </div>
        </div>
      </div>

      {/* Technicals */}
      {featuresToCompare.map((section) => (
        <div className="flex flex-col" key={section.title}>
          <h4 className="mb-1 text-lg font-bold sm:text-xl">{section.title}</h4>
          {section.items.map((feature) => (
            <div key={feature.name}>
              <h5 className="pt-2 mb-2 text-xs font-bold sm:text-sm">
                {feature.name}
              </h5>
              <div className="grid grid-cols-2 gap-7 pb-2 text-sm border-b sm:!gap-28 sm:text-base 2xs:gap-20">
                <p>{feature.left}</p>
                <p>{feature.right}</p>
              </div>
            </div>
          ))}
        </div>
      ))}
    </section>
  );
};
